import { z } from "zod";

// マスターCSV一括取り込みの要求と応答の形。
// サーバー側の同名ファイル(okumura-traceability-api/src/schema/console/master_import.ts)と同じ内容にする。
//
// ブラウザ側は zip の展開・CSV の解析・列の照合・値の型変換までを済ませてから送るため、
// ここで受け渡すのは「テーブルごとの行と、CSV 上の行番号」だけになる。
// 行番号は検証結果を利用者へ返すときに、元のファイルのどこが問題なのかを指し示すために使う。

// テーブル名。サーバー側は対象20テーブルの列挙で受けるが、ブラウザ側は送る前に
// masterCsvFiles がテーブルを特定済みのため、ここでは文字列として運ぶ。
export const masterImportCollectionKeySchema = z.string();

// CSV のセルから変換済みの値。文字列・数値・真偽値・未入力の4種類しか現れない。
export const masterImportCellValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const masterImportRowSchema = z.object({
  lineNumber: z.number().int(),
  values: z.record(masterImportCellValueSchema),
});

export const masterImportTableSchema = z.object({
  collectionKey: masterImportCollectionKeySchema,
  rows: z.array(masterImportRowSchema),
});

// 検証違反の種別。文言から性質を推し量る作りにすると説明文を書き直した途端に壊れるため、
// サーバーは種別を値として返す。naturalKeyDuplicate は「論理削除されていない行だけ一意」の
// 制約に引っかかった状態で、名前や整理番号のように取り込み時刻を付けても解消しなかったものに加えて、
// 枠の番号のように値を書き換えると意味が変わるため検査だけを行う一意キーの重複もここに入る。
// other はそれ以外のすべて。
export const masterImportErrorKindSchema = z.enum(["naturalKeyDuplicate", "other"]);

// 検証違反の位置と理由。ファイル全体に対する違反では行番号と列名が null になる。
export const masterImportErrorSchema = z.object({
  collectionKey: masterImportCollectionKeySchema,
  columnName: z.string().nullable(),
  kind: masterImportErrorKindSchema,
  lineNumber: z.number().nullable(),
  message: z.string(),
});

// 自然キーの重複を避けるために連番を付けた行。確認画面で利用者へ提示する。
export const masterImportRenameSchema = z.object({
  collectionKey: masterImportCollectionKeySchema,
  columnName: z.string(),
  fromValue: z.string(),
  lineNumber: z.number(),
  toValue: z.string(),
});

// 取り込み行の ID が既存行と重なった件数。テーブル単位で数える。
export const masterImportCollisionSchema = z.object({
  collectionKey: masterImportCollectionKeySchema,
  count: z.number(),
});

// 利用者が確認画面で承認した取り込み内容。確定の要求に添えて送る。
// 確認画面を出してから取り込むまでの間に別の利用者がマスタを変えると、承認していない名前の変更や
// 警告の無い上書きが混ざる。サーバーは確定の直前に計画を組み直し、この内容と突き合わせてから書き込む。
//
// idPolicy を承認へ含めるのは、承認が「どのIDの決め方に対するものか」まで含めて初めて成り立つため。
// 衝突と名前の変更だけを持つと、上書きを承認した内容がそのまま採番し直しの取り込みへ通ってしまう。
// 取り込み行の ID の決め方。要求と、利用者が承認した内容の両方に同じ値が載るため定義は1つにする。
// keepId はCSVに書かれたIDをそのまま使い、renumber は新しい番号を振り直す。
export const masterImportIdPolicySchema = z.enum(["keepId", "renumber"]);

export const masterImportApprovalSchema = z.object({
  collisions: z.array(masterImportCollisionSchema),
  idPolicy: masterImportIdPolicySchema,
  renames: z.array(masterImportRenameSchema),
});

// mode は取り込みを確定するかどうか、idPolicy は取り込み行の ID をどう決めるかを指定する。
// dryRun は apply と同じ処理を最後まで行ってから巻き戻すため、返る内容は apply と一致する。
// nameStamp は、名前の重複を避けるためにサーバーが名前の末尾へ付ける取り込み時刻。
// 試行の応答で返った値をそのまま確定の要求へ載せ、確認画面に出した名前と実際に書き込む名前をそろえる。
// 試行を経ずに送る場合は省略でき、そのときはサーバーが自分の現在時刻を使う。
// approval は利用者が承認した内容で、確定の要求にだけ意味がある。
// 試行を経ずに送る場合は省略でき、そのときは何も承認されていないものとして扱われる。
export const masterImportRequestSchema = z.object({
  approval: masterImportApprovalSchema.optional(),
  idPolicy: masterImportIdPolicySchema,
  mode: z.enum(["dryRun", "apply"]),
  nameStamp: z.string().optional(),
  tables: z.array(masterImportTableSchema),
});

// 実際に書き込んだ件数。dryRun では巻き戻すため、書き込む予定の件数を表す。
export const masterImportAppliedSchema = z.object({
  addedCount: z.number(),
  collectionKey: masterImportCollectionKeySchema,
  updatedCount: z.number(),
});

export const masterImportResponseSchema = z.object({
  applied: z.array(masterImportAppliedSchema),
  // 承認された内容と、確定の直前に組み直した計画が食い違ったときだけ true が付く。
  // 省略は食い違いが無かったことを表す。ブラウザ側はこの値だけで判断し、文言は読み取らない。
  // true のときは何も書き込まれておらず、collisions と renames には組み直した新しい内容が入る。
  approvalMismatch: z.boolean().optional(),
  collisions: z.array(masterImportCollisionSchema),
  errors: z.array(masterImportErrorSchema),
  // この取り込みで名前の末尾へ付けた取り込み時刻。確定の要求でそのまま送り返す
  nameStamp: z.string(),
  ok: z.boolean(),
  renames: z.array(masterImportRenameSchema),
});

export type MasterImportCellValue = z.infer<typeof masterImportCellValueSchema>;
export type MasterImportRow = z.infer<typeof masterImportRowSchema>;
export type MasterImportTable = z.infer<typeof masterImportTableSchema>;
export type MasterImportRequest = z.infer<typeof masterImportRequestSchema>;
export type MasterImportMode = MasterImportRequest["mode"];
export type MasterImportIdPolicy = MasterImportRequest["idPolicy"];
export type MasterImportErrorKind = z.infer<typeof masterImportErrorKindSchema>;
export type MasterImportError = z.infer<typeof masterImportErrorSchema>;
export type MasterImportRename = z.infer<typeof masterImportRenameSchema>;
export type MasterImportCollision = z.infer<typeof masterImportCollisionSchema>;
export type MasterImportApproval = z.infer<typeof masterImportApprovalSchema>;
export type MasterImportApplied = z.infer<typeof masterImportAppliedSchema>;
export type MasterImportResponse = z.infer<typeof masterImportResponseSchema>;

// 取り込みをサーバーへ依頼する呼び出し口の形。
// 画面はこの形だけに依存させ、通信の実体を差し替えられるようにする。
export type MasterImportRequester = (request: MasterImportRequest) => Promise<MasterImportResponse>;
